import type { components } from '@/generated/internal-api/openapi-types';
import { _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import { ResponseType } from '@/support/ApiService';

type ErrorResponseBody = components['schemas']['ErrorResponse.schema'];

/** One entry of the API's `errors` map, with its dotted key broken into parts. */
export interface ApiErrorFieldDetail {
  /** Raw server key, e.g. `treatment_types.0.name`. */
  path: string;
  /** Leading segment when the path addresses a collection, e.g. `treatment_types`. */
  collection?: string;
  /** Zero-based element index when the path contains one. */
  index?: number;
  /** Segments following the index, e.g. `name`. */
  field?: string;
  messages: Array<string>;
}

export interface ParsedApiError {
  /** The response body's top-level `message`, when present. */
  summary?: string;
  details: Array<ApiErrorFieldDetail>;
  status?: number;
}

interface ApiErrorLike {
  response?: { data?: ErrorResponseBody; status?: number };
  cause?: unknown;
}

const MAX_CAUSE_DEPTH = 3;

/**
 * `ApiService.call` rethrows an `InVivoError` that carries the original axios error on `cause`,
 * so the response detail sits one or more levels below the error the caller catches. The depth
 * cap is what stops a self-referencing `cause` chain from looping.
 */
const findApiResponse = (error: unknown, depth = 0): ApiErrorLike['response'] => {
  if (_isNil(error) || typeof error !== 'object' || depth > MAX_CAUSE_DEPTH) {
    return undefined;
  }

  const { response, cause } = error as ApiErrorLike;
  return _notNil(response?.data) ? response : findApiResponse(cause, depth + 1);
};

/**
 * Unwraps the `{ type: 'error', error }` envelope `ApiService.call` returns when
 * `onError.throw` is off, so a caller can hand either that response or a thrown error
 * to {@link parseApiErrorResponse}.
 */
const unwrapErrorResponse = (error: unknown): unknown => {
  if (_isNil(error) || typeof error !== 'object') {
    return error;
  }

  const { type, error: nested } = error as { type?: unknown; error?: unknown };
  return type === ResponseType.Error && _notNil(nested) ? nested : error;
};

/**
 * Splits a validation key into the parts a UI can label. The API reports keys as paths into
 * the request payload (`treatment_types.0.name`), so the index is what ties a message back to
 * the row the user entered.
 */
const parseFieldPath = (path: string): Omit<ApiErrorFieldDetail, 'messages'> => {
  const segments = path.split('.');
  const indexPosition = segments.findIndex((segment) => /^\d+$/.test(segment));

  if (indexPosition < 0) {
    return { path, field: _isNotBlank(path) ? path : undefined };
  }

  const collection = segments.slice(0, indexPosition).join('.');
  const field = segments.slice(indexPosition + 1).join('.');

  return {
    path,
    collection: _isNotBlank(collection) ? collection : undefined,
    index: Number(segments[indexPosition]),
    field: _isNotBlank(field) ? field : undefined,
  };
};

/** Coerces one `errors` map value into a list of messages, tolerating a bare string. */
const toMessages = (value: unknown): Array<string> => {
  const candidates = Array.isArray(value) ? value : [value];

  return candidates.reduce<Array<string>>((messages, candidate) => {
    if (typeof candidate === 'string' && _isNotBlank(candidate)) {
      messages.push(candidate);
    }
    return messages;
  }, []);
};

/**
 * Reads the API's standard error body — `{ message, errors }`, see `ErrorResponse.schema` — out
 * of anything a request rejected or resolved with, whether that is a raw axios error, the
 * `InVivoError` `ApiService.call` throws, or an `ApiErrorResponse` envelope.
 *
 * Returns empty `details` and no `summary` when the value carries no usable body, which is how
 * a caller detects that it has nothing better to show than its own generic message.
 */
export const parseApiErrorResponse = (error: unknown): ParsedApiError => {
  const response = findApiResponse(unwrapErrorResponse(error));
  const { message, errors } = response?.data ?? {};

  const details = Object.entries(errors ?? {}).reduce<Array<ApiErrorFieldDetail>>((acc, [path, value]) => {
    const messages = toMessages(value);
    if (_isNotEmpty(messages)) {
      acc.push({ ...parseFieldPath(path), messages });
    }
    return acc;
  }, []);

  return {
    ...(typeof message === 'string' && _isNotBlank(message) ? { summary: message } : {}),
    details,
    ...(typeof response?.status === 'number' ? { status: response.status } : {}),
  };
};
